// Mapeia as respostas (JSON:API, atributos dasherizados) dos endpoints de
// /reports/live_challenges/* para o formato já consumido pelos componentes
// desta tela. Ver doc em .claude/tasks/prd-relatorio-desafio-ao-vivo/ (ou o
// arquivo de referência manual dos endpoints) para o contrato completo.
import moment from 'moment';

export const TEACHERS_PER_PAGE = 20;
export const CHALLENGES_PER_PAGE = 20;

export function buildDateQuery(startDate, endDate) {
  return { initial_date: startDate, end_date: endDate };
}

// A API agrupa os pontos do gráfico de uso conforme o tamanho do período
// filtrado (initial_date/end_date): até 31 dias agrupa por dia, até 120 dias
// por semana (retornando o primeiro dia da semana) e acima disso por mês.
// Como a API não informa qual agrupamento usou, replicamos a mesma regra
// aqui pra formatar o label de cada ponto (`period-start`) coerentemente.
const USAGE_CHART_GROUPING_THRESHOLDS = { day: 31, week: 120 };

function resolveUsageChartGrouping(startDate, endDate) {
  if (!startDate || !endDate) return 'day';
  const rangeDays = moment(endDate).diff(moment(startDate), 'days') + 1;
  if (rangeDays <= USAGE_CHART_GROUPING_THRESHOLDS.day) return 'day';
  if (rangeDays <= USAGE_CHART_GROUPING_THRESHOLDS.week) return 'week';
  return 'month';
}

function formatUsageChartLabel(periodStart, grouping) {
  const start = moment(periodStart);
  if (grouping === 'week') {
    return `${start.format('DD/MM')} a ${start.clone().add(6, 'days').format('DD/MM')}`;
  }
  if (grouping === 'month') {
    return start.format('MM/YYYY');
  }
  return start.format('DD/MM');
}

export function buildUsageChartData(chartData = [], { startDate, endDate } = {}) {
  const grouping = resolveUsageChartGrouping(startDate, endDate);
  return {
    categories: chartData.map(item => formatUsageChartLabel(item['period-start'], grouping)),
    series: [{ label: 'Ativações por período', color: '#3956F5', data: chartData.map(item => item.count) }]
  };
}

export function mapGreDistribution(distribution = []) {
  return distribution.map(item => ({ name: item['regional-name'], value: item.count }));
}

export function mapSubjectDistribution(distribution = []) {
  return distribution
    .map(item => {
      const activationsCount = item['activations-count'] ?? 0;
      const accuracyPercentage = item['accuracy-percentage'] ?? 0;
      // Fracionário de propósito — não arredondar pra inteiro. Com poucas
      // ativações (ex.: 2), Math.round distorce a proporção visual da barra
      // (66,67%/33,33% virava 1/1 = 50%/50%). O echarts empilha floats sem
      // problema, então correct+incorrect continuam somando activationsCount
      // exatamente na mesma proporção do %.
      const correct = activationsCount * (accuracyPercentage / 100);
      return {
        subject: item['subject-name'],
        correct,
        incorrect: activationsCount - correct,
        // % pooled de acerto/erro vindas direto da API, usadas no tooltip.
        accuracyPercentage: item['accuracy-percentage'],
        errorPercentage: item['error-percentage'],
        activationsCount
      };
    })
    .sort((a, b) => (b.accuracyPercentage ?? 0) - (a.accuracyPercentage ?? 0));
}

export function mapTeachers(teachers = []) {
  return teachers.map(teacher => ({
    id: teacher.id,
    name: teacher.name,
    email: teacher.email,
    gre: teacher['regional-name'],
    institution: teacher['institution-name'],
    activations: teacher['activations-count'],
    avatar: null
  }));
}

function mapActivation(activation) {
  return {
    id: activation.id,
    label: `#${activation.position}`,
    date: activation['started-at'],
    participants: activation['participants-count'],
    accuracyPercent: activation['accuracy-percentage']
  };
}

export function mapChallenges(challenges = []) {
  return challenges.map(challenge => ({
    id: challenge.id,
    title: challenge.title,
    subject: challenge['subject-name'],
    createdByTeacher: challenge.creator,
    activations: challenge['activations-count'],
    avgParticipants: challenge['average-participants'],
    avgAccuracyPercent: challenge['accuracy-percentage'],
    lastActivationAt: challenge['last-activation-at'],
    activationsList: (challenge.activations || []).map(mapActivation)
  }));
}

export function mapRanking(ranking = []) {
  return ranking.map(student => ({
    id: student['participant-id'],
    position: student.position,
    name: student.name,
    email: student.email,
    institutionCourse: student.institution,
    classroom: student.classroom,
    points: student.score
  }));
}
